/**
 * Service for managing custom intro paragraphs
 * Uses Office.context.roamingSettings for cross-device persistence with localStorage fallback
 */

// Default intro paragraphs
const DEFAULT_INTRO_PARAGRAPHS = {
  'default': {
    id: 'default',
    name: 'Default',
    content: `My name is {{senderName}} and I am working in conjunction with {{primaryValuer}} on the referenced appraisal on behalf of {{clientName}}.
I have included a list of property data that we typically request for this property type.
Please understand that this is a "wish list", and I understand if you do not have all of this data.
Some items are more important than others, but everything you can provide will be appreciated.
Please gather and send the following documents to me at {{sigEmail}}`,
    isCustom: false,
    isEditable: false,
    createdDate: null
  }
};

// Fixed footer text that's always appended (non-editable)
const FIXED_FOOTER_TEXT = `You can email the documents to me at {{sigEmail}} or if the files are too large, please let me know and I can provide alternate means to share the files.`;

// Storage keys
const STORAGE_KEYS = {
  propertyContact: {
    CUSTOM_PARAGRAPHS: 'customIntroParagraphs',
    SELECTED_PARAGRAPH: 'selectedIntroParagraph'
  },
  clientIntroduction: {
    CUSTOM_PARAGRAPHS: 'customClientIntroStatements',
    SELECTED_PARAGRAPH: 'selectedClientIntroStatement'
  }
};

// Helper to get storage keys for a context
function getStorageKeys(context = 'propertyContact') {
  return STORAGE_KEYS[context] || STORAGE_KEYS.propertyContact;
}

/**
 * Initialize the intro paragraph service
 */
export async function initializeIntroParagraphService() {
  try {
    console.log('🔧 Initializing Intro Paragraph Service...');
    
    // Ensure we have a selected paragraph
    const selectedId = await getSelectedParagraphId();
    if (!selectedId) {
      await setSelectedParagraphId('default');
    }
    
    console.log('✅ Intro Paragraph Service initialized');
    return true;
  } catch (error) {
    console.error('❌ Failed to initialize Intro Paragraph Service:', error);
    return false;
  }
}

/**
 * Get all intro paragraphs (default + custom)
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<Array>} Array of intro paragraph objects
 */
export async function getAllIntroParagraphs(context = 'propertyContact') {
  try {
    const customParagraphs = await getCustomParagraphs(context);
    const allParagraphs = Object.values(DEFAULT_INTRO_PARAGRAPHS);
    
    // Add custom paragraphs at the top
    const customParagraphsArray = Object.values(customParagraphs);
    customParagraphsArray.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
    
    return [...customParagraphsArray, ...allParagraphs];
  } catch (error) {
    console.error('Error getting all intro paragraphs:', error);
    return Object.values(DEFAULT_INTRO_PARAGRAPHS);
  }
}

/**
 * Get custom paragraphs from storage
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<Object>} Object with custom paragraphs
 */
async function getCustomParagraphs(context = 'propertyContact') {
  try {
    const keys = getStorageKeys(context);
    // Try Office.js roaming settings first
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      const roamingData = Office.context.roamingSettings.get(keys.CUSTOM_PARAGRAPHS);
      if (roamingData) {
        console.log('📱 Loaded custom paragraphs from roaming settings');
        return JSON.parse(roamingData);
      }
    }
    
    // Fallback to localStorage
    const localData = localStorage.getItem(keys.CUSTOM_PARAGRAPHS);
    if (localData) {
      console.log('💾 Loaded custom paragraphs from localStorage');
      return JSON.parse(localData);
    }
    
    return {};
  } catch (error) {
    console.error('Error loading custom paragraphs:', error);
    return {};
  }
}

/**
 * Save custom paragraphs to storage
 * @param {Object} paragraphs - Custom paragraphs object
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<boolean>} Success status
 */
async function saveCustomParagraphs(paragraphs, context = 'propertyContact') {
  try {
    const keys = getStorageKeys(context);
    const dataString = JSON.stringify(paragraphs);
    
    // Save to Office.js roaming settings (cross-device)
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(keys.CUSTOM_PARAGRAPHS, dataString);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('✅ Custom paragraphs saved to roaming settings');
        } else {
          console.warn('⚠️ Failed to save to roaming settings, using localStorage only');
        }
      });
    }
    
    // Also save to localStorage as fallback
    localStorage.setItem(keys.CUSTOM_PARAGRAPHS, dataString);
    console.log('💾 Custom paragraphs saved to localStorage');
    
    return true;
  } catch (error) {
    console.error('Error saving custom paragraphs:', error);
    return false;
  }
}

/**
 * Create a new custom intro paragraph
 * @param {string} name - Name/title for the paragraph
 * @param {string} content - Content of the paragraph
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<Object>} Created paragraph object or null if failed
 */
export async function createCustomIntroParagraph(name, content, context = 'propertyContact') {
  try {
    const customParagraphs = await getCustomParagraphs(context);
    
    // Generate unique ID
    const id = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Create new paragraph
    const newParagraph = {
      id,
      name: name.trim(),
      content: content, // Don't trim HTML content as it can break formatting
      isCustom: true,
      isEditable: true,
      createdDate: new Date().toISOString()
    };
    
    // Add to custom paragraphs
    customParagraphs[id] = newParagraph;
    
    // Save to storage
    await saveCustomParagraphs(customParagraphs, context);
    
    console.log('✅ Created new custom intro paragraph:', newParagraph.name);
    return newParagraph;
  } catch (error) {
    console.error('❌ Failed to create custom intro paragraph:', error);
    throw error;
  }
}

/**
 * Update an existing custom intro paragraph
 * @param {string} id - Paragraph ID
 * @param {string} name - Updated name
 * @param {string} content - Updated content
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<Object>} Updated paragraph object or null if failed
 */
export async function updateCustomIntroParagraph(id, name, content, context = 'propertyContact') {
  try {
    const customParagraphs = await getCustomParagraphs(context);
    
    if (!customParagraphs[id]) {
      throw new Error('Custom paragraph not found');
    }
    
    if (!customParagraphs[id].isEditable) {
      throw new Error('This paragraph is not editable');
    }
    
    // Update paragraph
    customParagraphs[id] = {
      ...customParagraphs[id],
      name: name.trim(),
      content: content, // Don't trim HTML content as it can break formatting
      modifiedDate: new Date().toISOString()
    };
    
    // Save to storage
    await saveCustomParagraphs(customParagraphs, context);
    
    console.log('✅ Updated custom intro paragraph:', customParagraphs[id].name);
    return customParagraphs[id];
  } catch (error) {
    console.error('❌ Failed to update custom intro paragraph:', error);
    throw error;
  }
}

/**
 * Delete a custom intro paragraph
 * @param {string} id - Paragraph ID
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<boolean>} Success status
 */
export async function deleteCustomIntroParagraph(id, context = 'propertyContact') {
  try {
    const customParagraphs = await getCustomParagraphs(context);
    
    if (!customParagraphs[id]) {
      throw new Error('Custom paragraph not found');
    }
    
    if (!customParagraphs[id].isEditable) {
      throw new Error('This paragraph cannot be deleted');
    }
    
    // Check if this is the currently selected paragraph
    const selectedId = await getSelectedParagraphId(context);
    if (selectedId === id) {
      // Reset to default if deleting currently selected
      await setSelectedParagraphId('default', context);
    }
    
    // Delete paragraph
    delete customParagraphs[id];
    
    // Save to storage
    await saveCustomParagraphs(customParagraphs, context);
    
    console.log('✅ Deleted custom intro paragraph:', id);
    return true;
  } catch (error) {
    console.error('❌ Failed to delete custom intro paragraph:', error);
    throw error;
  }
}

/**
 * Get selected intro paragraph ID
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<string>} Selected paragraph ID
 */
export async function getSelectedParagraphId(context = 'propertyContact') {
  try {
    const keys = getStorageKeys(context);
    // Try Office.js roaming settings first
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      const roamingSelection = Office.context.roamingSettings.get(keys.SELECTED_PARAGRAPH);
      if (roamingSelection) {
        return roamingSelection;
      }
    }
    
    // Fallback to localStorage
    const localSelection = localStorage.getItem(keys.SELECTED_PARAGRAPH);
    return localSelection || 'default';
  } catch (error) {
    console.error('Error getting selected paragraph ID:', error);
    return 'default';
  }
}

/**
 * Set selected intro paragraph ID
 * @param {string} id - Paragraph ID to select
 * @param {string} context - Storage context ('propertyContact' or 'clientIntroduction')
 * @returns {Promise<boolean>} Success status
 */
export async function setSelectedParagraphId(id, context = 'propertyContact') {
  try {
    const keys = getStorageKeys(context);
    // Save to Office.js roaming settings
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(keys.SELECTED_PARAGRAPH, id);
      Office.context.roamingSettings.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          console.log('✅ Selected paragraph ID saved to roaming settings');
        }
      });
    }
    
    // Also save to localStorage
    localStorage.setItem(keys.SELECTED_PARAGRAPH, id);
    console.log('💾 Selected paragraph ID saved:', id);
    
    return true;
  } catch (error) {
    console.error('Error setting selected paragraph ID:', error);
    return false;
  }
}

/**
 * Get the currently selected intro paragraph
 * @returns {Promise<Object>} Selected paragraph object
 */
export async function getSelectedIntroParagraph() {
  try {
    const selectedId = await getSelectedParagraphId();
    const allParagraphs = await getAllIntroParagraphs();
    
    const selectedParagraph = allParagraphs.find(p => p.id === selectedId);
    return selectedParagraph || allParagraphs.find(p => p.id === 'default');
  } catch (error) {
    console.error('Error getting selected intro paragraph:', error);
    return DEFAULT_INTRO_PARAGRAPHS['default'];
  }
}

/**
 * Get the full intro content (selected paragraph + fixed footer)
 * @returns {Promise<string>} Complete intro content
 */
export async function getCompleteIntroContent() {
  try {
    const selectedParagraph = await getSelectedIntroParagraph();
    return `${selectedParagraph.content}\n\n${FIXED_FOOTER_TEXT}`;
  } catch (error) {
    console.error('Error getting complete intro content:', error);
    return `${DEFAULT_INTRO_PARAGRAPHS['default'].content}\n\n${FIXED_FOOTER_TEXT}`;
  }
}

/**
 * Get word count for text
 * @param {string} text - Text to count
 * @returns {number} Word count
 */
export function getWordCount(text) {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

export default {
  initializeIntroParagraphService,
  getAllIntroParagraphs,
  createCustomIntroParagraph,
  updateCustomIntroParagraph,
  deleteCustomIntroParagraph,
  getSelectedParagraphId,
  setSelectedParagraphId,
  getSelectedIntroParagraph,
  getCompleteIntroContent,
  getWordCount
};